import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import CommModalContainer from './CommModalContainer';
import { omit } from 'ui-addon-upgrades/utils';
import { correctUpgradeData, overrideAssignable, trackContactSalesButtonClick, trackContactSalesButtonView, trackCommModalClose } from 'ui-addon-upgrades/_core/contactSales/utils';
import { getIsRetailPortal } from 'ui-addon-upgrades/_core/common/api/fetchRetail';
import { getPropertyRepInfo } from 'ui-addon-upgrades/_core/common/data/upgradeData/transformers/getPropertyRepInfo';
import { prefetchMeetingData } from '../common/api/meetingHelpers';
import { ContactSalesButtonPropsInterface } from 'ui-addon-upgrades/_core/contactSales/ContactSalesPropsInterface';
import { sourceKeys } from 'ui-addon-upgrades/_core/common/data/upgradeData/properties/sources';
import { sequence } from 'ui-addon-upgrades/_core/contactSales/utils';
import { debounce } from 'ui-addon-upgrades/utils';
import { trackPotentialBannerInteraction } from 'ui-addon-upgrades/ums/utils';
import { getCanViewNewSeats } from '../common/eligibility/getCanViewNewSeats';
import { mapPurchaseMotionApiName } from '../../ums/utils';
import { getIsFreePortal } from '../common/eligibility/getIsFreePortal';
const MODAL_STATE = {
  CLOSED: 'CLOSED',
  LOADING: 'LOADING',
  OPEN: 'OPEN'
};

const correctUpgradeDataHOC = Component => props => {
  return /*#__PURE__*/_jsx(Component, Object.assign({}, props, {
    upgradeData: correctUpgradeData(props.upgradeData, props.canViewNewSeats),
    isAssignable: overrideAssignable(props.isAssignable)
  }));
};

const ContactSalesButton = props => {
  const {
    isAssignable = true,
    onClick = () => {},
    onCancel = () => {}
  } = props;
  const [repInfoPromise, setRepInfoPromise] = useState(undefined);
  const [isFreePortalPromise, setIsFreePortalPromise] = useState(undefined);
  const [isRetailPortalPromise, setIsRetailPortalPromise] = useState(undefined);
  const [modalState, setModalState] = useState(MODAL_STATE.CLOSED);
  const [canViewNewSeats, setCanViewNewSeats] = useState(undefined);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const isFreePortal = useRef();
  const isRetailPortal = useRef();
  const repInfo = useRef();
  useEffect(() => {
    if (typeof props.canViewNewSeats === 'undefined' && typeof canViewNewSeats === 'undefined') {
      getCanViewNewSeats().then(res => {
        setCanViewNewSeats(res);
        props.upgradeData.apiName = mapPurchaseMotionApiName(res, props.upgradeData.upgradeProduct, 'ContactSalesButtonNew', props.upgradeData.apiName);
      }).catch(() => {
        setCanViewNewSeats(false);
      });
    }
  }, [props.upgradeData, props.canViewNewSeats, canViewNewSeats]);
  useEffect(() => {
    if ((typeof props.canViewNewSeats !== 'undefined' || typeof canViewNewSeats !== 'undefined') && !hasTrackedView) {
      trackContactSalesButtonView(props);
      setHasTrackedView(true);
    }

    setRepInfoPromise(getPropertyRepInfo(props.upgradeData, sourceKeys.CONTACT_SALES_BUTTON, props));
  }, [props, canViewNewSeats, hasTrackedView]);
  useEffect(() => {
    if (isFreePortalPromise === undefined) {
      setIsFreePortalPromise(getIsFreePortal());
    }

    if (isRetailPortalPromise === undefined) {
      setIsRetailPortalPromise(getIsRetailPortal());
    }

    prefetchMeetingData();
  }, [isFreePortalPromise, isRetailPortalPromise]);
  const handleClick = useCallback(() => {
    setModalState(MODAL_STATE.LOADING);
    const {
      pointType,
      upgradeKey
    } = props.upgradeData;
    trackPotentialBannerInteraction(pointType, upgradeKey);
    Promise.all([repInfoPromise, isFreePortalPromise, isRetailPortalPromise]).then(([repInfoResponse, isFreePortalResponse, isRetailPortalResponse]) => {
      repInfo.current = repInfoResponse && repInfoResponse.repInfo ? repInfoResponse.repInfo : {};
      isFreePortal.current = isFreePortalResponse;
      isRetailPortal.current = isRetailPortalResponse;
    }).catch(() => {
      repInfo.current = {};
      isFreePortal.current = true;
      isRetailPortal.current = false;
      Raven.captureMessage('Communication methods modal opened without setting data');
    }).finally(() => {
      trackContactSalesButtonClick(props, Boolean(isRetailPortal.current), repInfo.current);
      setModalState(MODAL_STATE.OPEN);
    });
  }, [isFreePortalPromise, isRetailPortalPromise, props, repInfoPromise]);

  const handleClose = () => {
    trackCommModalClose(props.upgradeData, repInfo.current);
    setModalState(MODAL_STATE.CLOSED);
    onCancel();
  };

  const renderButton = () => {
    const CLICK_DELAY = 50;
    const debouncedHandleClick = debounce(handleClick, CLICK_DELAY);
    const onClickSequence = sequence(debouncedHandleClick, onClick);
    const propsToPass = omit(props, ['usageLimitBannerData', 'forwardedRef', 'onClick', 'upgradeData', 'allowModal', 'isAssignable', 'termId', 'additionalUnitQuantity', 'isUpgradePoint', '_textOverride', '_overrideCommunicationMethodKey', '_buttonOverride', '_overrideModalHeaderText', 'modalRoot', 'canViewNewSeats']);

    if (props._buttonOverride) {
      return /*#__PURE__*/_jsx("div", Object.assign({
        onClick: onClickSequence,
        "data-selenium": "contact-sales-button",
        "data-test-id": "contact-sales-button-override",
        "data-reagan-primary-cta": true
      }, propsToPass, {
        children: props._buttonOverride
      }));
    } else {
      return /*#__PURE__*/_jsx(UILoadingButton, Object.assign({
        loading: modalState === MODAL_STATE.LOADING,
        buttonRef: props.forwardedRef,
        onClick: onClickSequence,
        "data-selenium": "contact-sales-button",
        "data-test-id": "contact-sales-button",
        "data-reagan-primary-cta": true
      }, propsToPass, {
        children: props._textOverride || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.contactSalesButton.text"
        })
      }));
    }
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderButton(), modalState === MODAL_STATE.OPEN && /*#__PURE__*/_jsx(CommModalContainer, {
      handleClose: handleClose,
      isFreePortal: Boolean(isFreePortal.current),
      isRetailPortal: Boolean(isRetailPortal.current),
      isAssignable: isAssignable,
      modalRoot: props.modalRoot,
      upgradeData: props.upgradeData,
      repInfo: repInfo.current,
      _overrideCommunicationMethodKey: props._overrideCommunicationMethodKey,
      _overrideModalHeaderText: props._overrideModalHeaderText,
      isUpgradePoint: props.isUpgradePoint
    })]
  });
};

ContactSalesButton.defaultProps = {
  use: 'primary'
};
ContactSalesButton.propTypes = ContactSalesButtonPropsInterface;
export default correctUpgradeDataHOC(ContactSalesButton);